import { startCase, toLower } from "lodash";
import { useRouter } from "next/router";
import styled from "styled-components";

import { trackAction } from "~/analytics";
import * as DetailList from "~/components/DetailList";
import { Box, Padding } from "~/components/Elements";
import Issuer from "~/components/Issuer";
import SkeletonDetailList from "~/components/SkeletonDetailList";
import getCountry from "~/data/getCountry";
import {
  GreenBox,
  GreenBoxButton,
  GreenBoxButtonText,
  GreenBoxSubTitle,
  GreenBoxTitle,
} from "~/templates/companies/styles";
import { BlurredDiv } from "~/templates/companies/styles";
import { VASP } from "~/types/vaspid";

const EMPTY = "-";

const VerticallyAlignedDiv = styled.div`
  display: flex;
  align-items: center;
  line-height: normal;
`;

const Container = styled.div`
  width: 100%;
`;

export default function VASPLicenseRegistration({
  vasp,
  userIsAuthenticated = false,
}: {
  vasp?: VASP | null;
  userIsAuthenticated: boolean;
}) {
  const router = useRouter();

  const handleJoinToday = async () => {
    if (vasp) {
      router.push(`/auth?redirect=/dashboard/companies/${vasp.did}`);
      trackAction("visitor-clicked-join-for-free", { group_vasp: vasp.did });
    }
  };

  const contentConfigs = vasp
    ? [
        {
          label: "Is Regulated",
          issuer: vasp.issuers?.isRegulated?.issuerName,
          issuedDate: vasp.issuers?.isRegulated?.issuedDate || vasp.updatedAt,
          value: startCase(toLower(vasp.isRegulated || undefined)),
        },
        {
          label: "Regulatory Status",
          issuer: vasp.issuers?.regulatoryStatus?.issuerName,
          issuedDate: vasp.issuers?.regulatoryStatus?.issuedDate || vasp.updatedAt,
          value: vasp.regulatoryStatus,
        },
        {
          label: "Jurisdiction",
          issuer: vasp.issuers?.jurisdictions?.issuerName,
          issuedDate: vasp.issuers?.jurisdictions?.issuedDate || vasp.updatedAt,
          value: getCountry(vasp.jurisdictions),
        },
        {
          label: "Supervisory Authority",
          issuer: vasp.issuers?.supervisoryAuthority?.issuerName,
          issuedDate: vasp.issuers?.supervisoryAuthority?.issuedDate || vasp.updatedAt,
          value: vasp.supervisoryAuthority,
        },
        {
          label: "Registration License ID",
          issuer: vasp.issuers?.registrationLicenseId?.issuerName,
          issuedDate: vasp.issuers?.registrationLicenseId?.issuedDate || vasp.updatedAt,
          value: vasp.registrationLicenseId,
        },
        {
          label: "Status Start Date",
          issuer: vasp.issuers?.statusStartDate?.issuerName,
          issuedDate: vasp.issuers?.statusStartDate?.issuedDate || vasp.updatedAt,
          value: vasp.statusStartDate ? new Date(vasp.statusStartDate).toLocaleDateString("en-CA") : undefined,
        },
        {
          label: "Status Expiration Date",
          issuer: vasp.issuers?.statusExpirationDate?.issuerName,
          issuedDate: vasp.issuers?.statusExpirationDate?.issuedDate || vasp.updatedAt,
          value: vasp.statusExpirationDate
            ? new Date(vasp.statusExpirationDate).toLocaleDateString("en-CA")
            : undefined,
        },
        {
          label: "Last Checked",
          issuer: vasp.issuers?.lastChecked?.issuerName,
          issuedDate: vasp.issuers?.lastChecked?.issuedDate || vasp.updatedAt,
          value: vasp.lastChecked ? new Date(vasp.lastChecked).toLocaleDateString("en-CA") : undefined,
        },
        {
          label: "Additional Information",
          issuer: vasp.issuers?.additionalInformation?.issuerName,
          issuedDate: vasp.issuers?.additionalInformation?.issuedDate || vasp.updatedAt,
          value: vasp.additionalInformation,
          tabletSize: 12,
          desktopSize: 12,
        },
      ]
    : [];

  return (
    <Box as={Container}>
      <Padding sized="medium">
        <DetailList.Header>
          <DetailList.Item mobile={12}>
            <DetailList.Title as="h2">License and Registration Information</DetailList.Title>
          </DetailList.Item>
        </DetailList.Header>
        <div style={{ position: "relative" }}>
          <BlurredDiv userIsAuthenticated={userIsAuthenticated}>
            {vasp ? (
              <DetailList.Content>
                {contentConfigs.map(({ label, issuer, issuedDate, value, tabletSize = 6, desktopSize = 3 }) => {
                  return (
                    <DetailList.Item key={label} mobile={12} tablet={tabletSize} desktop={desktopSize}>
                      <VerticallyAlignedDiv>
                        <DetailList.Term>{label}</DetailList.Term>
                      </VerticallyAlignedDiv>
                      <DetailList.Description className="localize-ignore">{value || EMPTY}</DetailList.Description>
                      {value && (
                        <Issuer
                          shortLabel={
                            issuer === "VASPnet"
                              ? "VERIFIED BY VASPNET"
                              : issuer === "GLEIF"
                              ? "VERIFIED BY GLEIF"
                              : "SELF-REPORTED"
                          }
                          longLabel={
                            issuer === "VASPnet" || issuer === "GLEIF"
                              ? "VERIFIED INFORMATION"
                              : "SELF-REPORTED INFORMATION"
                          }
                          name={issuer || vasp.name || ""}
                          issuedDate={issuedDate}
                          logo={`/assets/${
                            issuer === "VASPnet" ? "vasp-net-short" : issuer === "GLEIF" ? "gleif" : "self-report-logo"
                          }.svg`}
                        />
                      )}
                    </DetailList.Item>
                  );
                })}
              </DetailList.Content>
            ) : (
              <SkeletonDetailList count={8} />
            )}
          </BlurredDiv>
          {!userIsAuthenticated && (
            <GreenBox>
              <GreenBoxTitle>{`Is ${vasp?.name} regulated?`}</GreenBoxTitle>
              <GreenBoxSubTitle>{`Find out if ${vasp?.name} is regulated by signing up to Notabene’s free VASP network!`}</GreenBoxSubTitle>
              <GreenBoxButton onClick={handleJoinToday}>
                <GreenBoxButtonText>JOIN FOR FREE TODAY</GreenBoxButtonText>
              </GreenBoxButton>
            </GreenBox>
          )}
        </div>
      </Padding>
    </Box>
  );
}
